import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';

// ============================================================================
// GLOBAL window.alert OVERRIDE FOR ELECTRON
// ============================================================================
// On Windows Electron, window.alert() creates a native dialog that permanently
// steals focus from the webContents. After dismissing the dialog, input fields
// stop responding to clicks and keyboard — the user is stuck until they restart
// the app. This is Electron bug #19977.
//
// Fix: replace window.alert with a non-blocking in-page toast notification
// when running inside Electron. Regular browser users are unaffected.
//
// This single override catches ALL alert() calls across every page/component
// (MenuManagement, Profile, OpeningHours, Orders, etc.) without needing to
// edit each file individually.
if (window.electronAPI?.isElectron) {
  window.alert = (message) => {
    const toast = document.createElement('div');
    toast.textContent = String(message || '');
    const isError = /fail|error|invalid|must be|cannot|please/i.test(message);
    const bg = isError ? '#FEF2F2' : '#F0FDF4';
    const border = isError ? '#FECACA' : '#BBF7D0';
    const color = isError ? '#B91C1C' : '#15803D';
    toast.style.cssText = `position:fixed;top:20px;left:50%;transform:translateX(-50%);z-index:99999;padding:14px 28px;background:${bg};border:1px solid ${border};border-radius:12px;color:${color};font-weight:600;font-size:14px;box-shadow:0 4px 20px rgba(0,0,0,0.12);max-width:500px;text-align:center;animation:slideDown 0.3s ease;`;
    // Add animation
    if (!document.getElementById('alert-toast-style')) {
      const style = document.createElement('style');
      style.id = 'alert-toast-style';
      style.textContent = '@keyframes slideDown{from{transform:translateX(-50%) translateY(-20px);opacity:0}to{transform:translateX(-50%) translateY(0);opacity:1}}';
      document.head.appendChild(style);
    }
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.transition = 'opacity 0.3s';
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, 4000);
  };
}

// ============================================================================
// GLOBAL window.confirm OVERRIDE FOR ELECTRON
// ============================================================================
// Same issue as alert(): window.confirm() creates a native Win32 dialog that
// steals focus permanently. Unlike alert(), confirm() returns a boolean, so
// we can't replace it with a toast. Instead, route it through Electron's
// dialog.showMessageBoxSync() via IPC — that dialog properly returns focus
// to webContents after dismissal.
if (window.electronAPI?.showConfirmSync) {
  window.confirm = (message) => {
    return window.electronAPI.showConfirmSync(message);
  };
}

// ============================================================================
// UNIVERSAL FOCUS RECOVERY FOR ALL NATIVE DIALOGS
// ============================================================================
// On Windows Electron, EVERY native Win32 dialog steals focus permanently:
//   - alert() → fixed above with toast override
//   - confirm() → fixed above with IPC dialog
//   - <input type="file"> file chooser → STILL a native dialog
//   - window.print() → overridden by Electron silent print
//
// The file chooser is the last remaining native dialog we can't override
// (it's triggered by browser internals, not JS). Fix: detect when a file
// input dialog closes and call the main process to blur→focus the window,
// which re-establishes the webContents focus chain.
//
// We also add a universal mousedown handler as the ultimate safety net —
// if focus is ever lost for ANY reason, the first click recovers it.
if (window.electronAPI?.refocusWindow) {
  // After file dialog closes (file selected)
  document.addEventListener('change', (e) => {
    if (e.target?.type === 'file') {
      setTimeout(() => window.electronAPI.refocusWindow(), 100);
    }
  }, true);

  // After file dialog closes (canceled — fires 'cancel' event in Chrome 91+)
  document.addEventListener('cancel', (e) => {
    if (e.target?.type === 'file') {
      setTimeout(() => window.electronAPI.refocusWindow(), 100);
    }
  }, true);

  // Universal safety net: on any mousedown, if the active element is the
  // body (meaning nothing has focus), trigger refocus. This catches ANY
  // native dialog we haven't explicitly handled.
  document.addEventListener('mousedown', () => {
    if (document.activeElement === document.body || !document.activeElement) {
      window.electronAPI.refocusWindow();
    }
  }, true);
}

// React now handles all routes including authentication
// Static HTML pages are no longer used
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
// Build timestamp: 1775130645
